import React, { useState, useEffect } from 'react';
import { PreLaunchPage } from './pages/PreLaunchPage';
import {
  LandingPage,
  BlogPage,
  BlogPostPage,
  RegisterPage,
  EmailVerificationPage,
  LoginPage,
  PasswordResetPage,
  DemoNavigation,
  WriterOnboardingPage,
  WriterProfileEditPage,
  WriterDashboardPage,
  MyManuscriptsPage,
  CreateManuscriptPage,
  EditManuscriptPage,
  ManuscriptDetailPage,
  BetaApplicationsPage,
  ActiveBetaReadersPage,
  BetaFeedbackPage,
  AgentDirectoryPage,
  AgentProfilePage,
  SubmitQueryPage,
  MyQueriesPage,
  QueryDetailPage,
  ReaderOnboardingPage,
  ReaderProfileEditPage,
  ReaderProfilePage,
  ReaderDashboardPage,
  DiscoverManuscriptsPage,
  ManuscriptPreviewPage,
  ApplyToReadPage,
  MyApplicationsPage,
  CurrentReadsPage,
  ManuscriptReaderPage,
  SubmitFeedbackPage,
  ReadingHistoryPage,
  AgentOnboardingPage,
  AgentProfileEditPage,
  AgentDashboardPage,
  QueryInboxPage,
  AgentQueryDetailPage,
  SendResponsePage,
  RequestedManuscriptsPage,
  AgentManuscriptReaderPage,
  MSWLManagementPage,
  AgencyDashboardPage,
  AgencyProfileEditPage,
  ManageAgentsPage,
  AgencySettingsPage,
  ConversationsListPage,
  ConversationThreadPage,
  NewMessagePage,
  NotificationsCenterPage,
  NotificationSettingsPage,
  SettingsPage,
} from './pages';
import DemoAccountSwitcher from './components/DemoAccountSwitcher';

const PUBLIC_MARKETING_PATHS = new Set([
  "/",
  "/blog",
  "/blog/beta-reader-brief-template",
  "/blog/the-power-of-beta-readers-how-feedback-shapes-great-stories",
  "/blog/building-your-author-platform-before-publication",
  "/blog/what-literary-agents-really-look-for-in-query-letters",
  "/blog/how-to-give-meaningful-beta-reader-feedback",
  "/blog/the-beta-reading-process-what-to-expect",
  "/blog/common-manuscript-mistakes-and-how-to-fix-them"
]);

// Simple router component
// In a real app, you'd use React Router or similar
function App() {
  const [hasAccess, setHasAccess] = useState(false);
  const path = window.location.pathname.replace(/\/+$/, '') || '/';

  useEffect(() => {
    let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      document.head.appendChild(robots);
    }
    robots.content = PUBLIC_MARKETING_PATHS.has(path) ? 'index,follow' : 'noindex,nofollow';
  }, [path]);

  // Check for access on mount
  useEffect(() => {
    const access = localStorage.getItem('demoAccess');
    if (access === 'granted') {
      setHasAccess(true);
    }
  }, []);

  // Keep the public homepage consistent, including in browsers with saved demo access.
  if (path === '/') return <PreLaunchPage />;

  // Allow blog to be accessible without access
  if (path === '/blog') {
    return <BlogPage />;
  }

  // Allow individual blog posts to be accessible without access
  if (path.match(/^\/blog\/[a-z0-9-]+$/)) {
    return <BlogPostPage />;
  }

  // If no access, show pre-launch page
  if (!hasAccess) {
    return <PreLaunchPage />;
  }

  // Check for dynamic routes first
  // Using [a-zA-Z0-9-]+ to match UUIDs and other IDs
  if (path.match(/^\/writer\/manuscripts\/[a-zA-Z0-9-]+\/applications$/)) {
    return (
      <>
        <BetaApplicationsPage />
        <DemoAccountSwitcher />
      </>
    );
  }
  if (path.match(/^\/writer\/manuscripts\/[a-zA-Z0-9-]+\/feedback$/)) {
    return (
      <>
        <BetaFeedbackPage />
        <DemoAccountSwitcher />
      </>
    );
  }
  if (path.match(/^\/writer\/manuscripts\/[a-zA-Z0-9-]+\/edit$/)) {
    return (
      <>
        <EditManuscriptPage />
        <DemoAccountSwitcher />
      </>
    );
  }
  if (path.match(/^\/writer\/manuscripts\/[a-zA-Z0-9-]+$/)) {
    return (
      <>
        <ManuscriptDetailPage />
        <DemoAccountSwitcher />
      </>
    );
  }
  if (path.match(/^\/agents\/[a-zA-Z0-9-]+$/)) {
    return (
      <>
        <AgentProfilePage />
        <DemoAccountSwitcher />
      </>
    );
  }
  if (path.match(/^\/writer\/queries\/[a-zA-Z0-9-]+$/)) {
    return (
      <>
        <QueryDetailPage />
        <DemoAccountSwitcher />
      </>
    );
  }
  if (path.match(/^\/manuscripts\/[a-zA-Z0-9-]+\/preview$/)) {
    return (
      <>
        <ManuscriptPreviewPage />
        <DemoAccountSwitcher />
      </>
    );
  }
  if (path.match(/^\/manuscripts\/[a-zA-Z0-9-]+\/apply$/)) {
    return (
      <>
        <ApplyToReadPage />
        <DemoAccountSwitcher />
      </>
    );
  }
  if (path.match(/^\/reader\/manuscripts\/[a-zA-Z0-9-]+\/read$/)) {
    return (
      <>
        <ManuscriptReaderPage />
        <DemoAccountSwitcher />
      </>
    );
  }
  if (path.match(/^\/reader\/manuscripts\/[a-zA-Z0-9-]+\/feedback$/)) {
    return (
      <>
        <SubmitFeedbackPage />
        <DemoAccountSwitcher />
      </>
    );
  }
  if (path.match(/^\/agent\/queries\/[a-zA-Z0-9-]+\/respond$/)) {
    return (
      <>
        <SendResponsePage />
        <DemoAccountSwitcher />
      </>
    );
  }
  if (path.match(/^\/agent\/queries\/[a-zA-Z0-9-]+$/)) {
    return (
      <>
        <AgentQueryDetailPage />
        <DemoAccountSwitcher />
      </>
    );
  }
  if (path.match(/^\/agent\/manuscripts\/[a-zA-Z0-9-]+\/read$/)) {
    return (
      <>
        <AgentManuscriptReaderPage />
        <DemoAccountSwitcher />
      </>
    );
  }
  if (path.match(/^\/reader\/[a-zA-Z0-9-]+$/)) {
    return (
      <>
        <ReaderProfilePage />
        <DemoAccountSwitcher />
      </>
    );
  }
  if (path === '/messages/new') {
    return (
      <>
        <NewMessagePage />
        <DemoAccountSwitcher />
      </>
    );
  }
  if (path.match(/^\/messages\/[a-zA-Z0-9-]+$/)) {
    return (
      <>
        <ConversationThreadPage />
        <DemoAccountSwitcher />
      </>
    );
  }

  let PageComponent;

  // Route to appropriate page
  switch (path) {
    case '/':
      PageComponent = LandingPage;
      break;
    case '/blog':
      PageComponent = BlogPage;
      break;
    case '/demo':
      PageComponent = DemoNavigation;
      break;
    case '/register':
      PageComponent = RegisterPage;
      break;
    case '/verify-email':
      PageComponent = EmailVerificationPage;
      break;
    case '/login':
      PageComponent = LoginPage;
      break;
    case '/reset-password':
      PageComponent = PasswordResetPage;
      break;
    case '/onboarding/writer':
      PageComponent = WriterOnboardingPage;
      break;
    case '/onboarding/reader':
      PageComponent = ReaderOnboardingPage;
      break;
    case '/onboarding/agent':
      PageComponent = AgentOnboardingPage;
      break;
    case '/writer/profile/edit':
      PageComponent = WriterProfileEditPage;
      break;
    case '/agent/profile/edit':
      PageComponent = AgentProfileEditPage;
      break;
    case '/reader/profile/edit':
      PageComponent = ReaderProfileEditPage;
      break;
    case '/reader/dashboard':
      PageComponent = ReaderDashboardPage;
      break;
    case '/manuscripts/discover':
      PageComponent = DiscoverManuscriptsPage;
      break;
    case '/writer/dashboard':
      PageComponent = WriterDashboardPage;
      break;
    case '/writer/manuscripts':
      PageComponent = MyManuscriptsPage;
      break;
    case '/writer/manuscripts/new':
      PageComponent = CreateManuscriptPage;
      break;
    case '/writer/beta-readers':
      PageComponent = ActiveBetaReadersPage;
      break;
    case '/agents':
      PageComponent = AgentDirectoryPage;
      break;
    case '/writer/query/new':
      PageComponent = SubmitQueryPage;
      break;
    case '/writer/queries':
      PageComponent = MyQueriesPage;
      break;
    case '/reader/applications':
      PageComponent = MyApplicationsPage;
      break;
    case '/reader/manuscripts':
      PageComponent = CurrentReadsPage;
      break;
    case '/reader/history':
      PageComponent = ReadingHistoryPage;
      break;
    case '/agent/dashboard':
      PageComponent = AgentDashboardPage;
      break;
    case '/agent/queries':
      PageComponent = QueryInboxPage;
      break;
    case '/agent/manuscripts':
      PageComponent = RequestedManuscriptsPage;
      break;
    case '/agent/mswl':
      PageComponent = MSWLManagementPage;
      break;
    case '/agency/dashboard':
      PageComponent = AgencyDashboardPage;
      break;
    case '/agency/profile/edit':
      PageComponent = AgencyProfileEditPage;
      break;
    case '/agency/agents':
      PageComponent = ManageAgentsPage;
      break;
    case '/agency/settings':
      PageComponent = AgencySettingsPage;
      break;
    case '/messages':
      PageComponent = ConversationsListPage;
      break;
    case '/notifications':
      PageComponent = NotificationsCenterPage;
      break;
    case '/settings/notifications':
      PageComponent = NotificationSettingsPage;
      break;
    case '/settings':
      PageComponent = SettingsPage;
      break;
    default:
      PageComponent = LandingPage;
  }

  return (
    <>
      <PageComponent />
      <DemoAccountSwitcher />
    </>
  );
}

export default App;
